import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '~/Layouts';
import {
    Container,
    PageHeader,
    PageBanner,
    Pagination,
    SwitchView,
    Tabs
} from '~/Common';
import {
    Box,
    Flex,
    Grid,
    Heading,
    Spinner,
    Text
} from '@theme-ui/components'
import {
    Card,
    CardCompact,
} from '~/Deals';
import {
    List
} from '~/Investments';
import { Icon } from 'assets/Icon';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { setItemLayout } from 'features/theme/themeSlice';
import { getInstitutionalDeals, getInstitutionalDealsAsync, getInstitutionClosedDealsAsync } from 'features/deal/dealSlice';
import { APP_FEATURES, DealStatus } from 'app/constants';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { InvestmentsList, Invites, InviteUserForm, Summary, Users } from '~/Institutions';
import { getPortfolioStatsAsync } from 'features/portfolio/portfolioSlice';
import { VerifyStepsInstitutional } from '~/Common/VerifySteps';

const mapStateToProps = (state) => {
    return { user: state.user?.user, institutionUsers: state.user?.user?.institution?.users, invites: state.invites, theme: state.theme, deals: state.deals, portfolio: state.portfolio }
}

const InstitutionDashboard = ({ user, institutionUsers, invites, theme, deals, portfolio }) => {
    const { t } = useTranslation();
    useDocumentTitle(t('Institution Dashboard'));
    const dispatch = useDispatch();
    const [selectedTab, setSelectedTab] = useState('institution');
    const [loading, setLoading] = useState(true);

    const changeItemLayout = (e, type) => {
        e.preventDefault();
        setLoading(true);

        setTimeout(() => {
            dispatch(setItemLayout(type));
            setLoading(false)
        }, 500);
    };

    const tabs = [
        {
            key: 'institution',
            name: 'My Institution',
            icon: 'office-building',
            onClick: () => setSelectedTab('institution')
        },
        {
            key: 'deals',
            name: 'Live Deals',
            icon: 'deals',
            onClick: () => setSelectedTab('deals')
        }
    ];

    useEffect(() => {
        if (deals?.deals.institutional.status === 'idle') {
            dispatch(getInstitutionalDealsAsync());
        }
        if (deals?.deals.institutional_closed.status === 'idle') {
            dispatch(getInstitutionClosedDealsAsync());
        }
        if (deals?.deals.institutional.status === 'attempted' && deals?.deals.institutional_closed.status === 'attempted'){
            setLoading(false);
        }
        if (portfolio.stats.status === 'idle') {
            dispatch(getPortfolioStatsAsync());
        }
    }, [deals, setLoading, dispatch, portfolio.stats.status]);

    return (
        <DashboardLayout p={0}>
            <PageBanner img="2">
                <Heading as="h1" mt={5} mb={4} sx={{
                    color: t => t.colors.white
                }}>{t('dashboard.welcome', { name: user.first_name })}</Heading>

                <Summary user={user} institution={user?.institution} />
            </PageBanner>
            <PageHeader />
            <VerifyStepsInstitutional />
            <Container variant="layouts.default">
                <Tabs sx={{ mb: '40px' }} tabs={tabs} selected={selectedTab} />

                {selectedTab === 'institution' ? <>
                    <InvestmentsList portfolio={portfolio} />

                    <Grid sx={{
                        alignItems: 'center',
                        gridTemplate: 'auto / repeat(2, 1fr)',
                        borderBottom: '1px solid',
                        borderColor: 'medium',
                        py: 3,
                        mb: 3
                    }}>
                        <Heading as="h2" variant="h3" id="myInstitution">{t('My Institution')}</Heading>
                    </Grid>
                    <Box mb={'38px'}>
                        {t('Below is the list of users currently linked to your institution.')}
                    </Box>

                    <Users institutionUsers={institutionUsers} />

                    {APP_FEATURES.adviser_invites ? (
                        <>
                            <Box py={3}>
                                <InviteUserForm setLoading={setLoading} />
                            </Box>

                            <Invites invites={invites?.invites} />
                        </>
                    ) : null}
                </> : null}
                {selectedTab === 'deals' ? <>
                    <Grid sx={{
                        alignItems: 'center',
                        gridTemplate: 'auto / repeat(2, 1fr)',
                        borderBottom: '1px solid',
                        borderColor: 'medium',
                        py: 3,
                        mb: 3
                    }}>
                        <Heading as="h2" variant="h3" id="myDeals">{t('Live Deals')}</Heading>
                        <SwitchView selected={theme.itemLayout} onChange={changeItemLayout} />
                    </Grid>
                    <Box mb={'38px'}>
                        {t('Below is the list of deals live on Liquidity now.')}
                    </Box>

                    <Flex mb={[4, 5, 6]} sx={{
                        flexDirection: 'column',
                        position: 'relative',
                    }}>
                        {loading &&
                            <Flex sx={{
                                flexDirection: 'column',
                                position: 'absolute',
                                height: '100%',
                                width: '100%',
                                bg: 'rgba(255, 255, 255, 0.5)',
                                left: 0,
                                top: 0,
                                zIndex: 100
                            }}>
                                <Spinner variant="styles.spinner" mx={'auto'} mt={4} />
                            </Flex>
                        }

                        {deals.deals.institutional.results.length > 0 &&
                            <>
                                <Grid sx={{
                                    gridGap: 4,
                                    gridTemplateColumns: theme.itemLayout === 'grid'
                                        ? ['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)']
                                        : '1fr',
                                    gridTemplateRows: '1fr',
                                    width: '100%',
                                    overflowX: 'auto',
                                    pt: '10px',
                                    pb: '30px',
                                    px: 3,
                                    mx: -3,
                                    boxSizing: 'content-box',
                                }}>
                                    {deals.deals.institutional.results.map((deal, key) => (
                                        <Box key={`deals-active-managed-result${key}`}>
                                            {theme.itemLayout === 'grid' ? (
                                                <Card hideCardLink={true} deal={deal} />
                                            ) : (
                                                <List hideCardLink={true} deal={deal} />
                                            )}
                                        </Box>
                                    ))}
                                </Grid>

                                <Pagination
                                    activePage={deals.deals?.institutional?.meta?.current_page ?? 0}
                                    pageCount={deals.deals?.institutional?.meta?.last_page ?? 0}
                                    onPageChange={(event) => {
                                        const payload = getInstitutionalDeals({ params: {
                                            page: (event.selected + 1)
                                        }});
                                        setLoading(true);
                                        dispatch(getInstitutionalDealsAsync(payload))
                                    }}
                                />
                            </>
                        }

                        {(!loading && deals.deals.institutional.results.length === 0) &&
                            <Box px={2}>
                                <Flex sx={{
                                    py: 5,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                }}>
                                    <Icon icon={'lock-thin'} color="tealDark" mb={'28px'} width="84px" />
                                    <Heading variant="h2" sx={{ fontSize: '36px' }}>{t('deals.institutional.notFoundHeading')}</Heading>
                                    <Text mt={1} variant="subtext">{t('deals.institutional.notFound')}</Text>
                                </Flex>
                            </Box>
                        }
                    </Flex>

                    {deals.deals.institutional_closed.results.length > 0 ?
                        <Box>
                            <Flex sx={{
                                borderBottom: '1px solid',
                                borderColor: 'medium',
                                mb: 4,
                                pb: 1
                            }}>
                                <Heading as="h2" variant="h3" id="pastDeals">{t('Past Deals')}</Heading>
                            </Flex>

                            <Grid sx={{
                                gridGap: 4,
                                gridTemplateColumns: theme.itemLayout === 'grid' ? ['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)', 'repeat(4, 1fr)'] : '1fr',
                                gridTemplateRows: '1fr',
                                width: '100%',
                                overflowX: 'auto',
                                pt: '10px',
                                pb: '30px',
                                px: 3,
                                mx: -3,
                                boxSizing: 'content-box',
                            }}>
                                {deals.deals.institutional_closed.results.map((row, key) => (
                                    <Box key={key}>
                                        {theme.itemLayout === 'grid' ? (
                                            <CardCompact deal={row} sx={{ height: '100%' }} />
                                        ) : (
                                            <List deal={row} />
                                        )}
                                    </Box>
                                ))}
                            </Grid>

                            <Pagination
                                activePage={deals.deals?.institution_closed?.meta?.current_page ?? 0}
                                pageCount={deals.deals?.institution_closed?.meta?.last_page ?? 0}
                                onPageChange={(event) => {
                                    const payload = getInstitutionalDeals({ params: {
                                        status: DealStatus.closed,
                                        page: (event.selected + 1)
                                    }});
                                    setLoading(true);
                                    dispatch(getInstitutionClosedDealsAsync(payload))
                                }}
                            />
                        </Box>
                    : null}
                </> : null}
            </Container>
        </DashboardLayout>
    );
};

export default connect(mapStateToProps, null)(InstitutionDashboard);
